import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc, setDoc, deleteDoc, getDocs, writeBatch, query, where } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth, db } from '../firebase';
import Navbar from '../components/Navbar';

const TRIAL_DAYS = 5;

function getTrialStatus(company) {
  if (company.paid) return { label: 'Paid', color: '#818cf8', ended: false, isLegacy: false, isPaid: true };
  const createdAt = company.createdAt?.toDate?.();
  if (!createdAt) return { label: 'Legacy', color: '#888', ended: false, isLegacy: true };
  const trialEndsAt = new Date(createdAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const daysLeft = Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 0) return { label: 'Trial Ended', color: '#f87171', ended: true, isLegacy: false };
  if (daysLeft === 1) return { label: '1 day left', color: '#fb923c', ended: false, isLegacy: false };
  return { label: `${daysLeft} days left`, color: '#4ade80', ended: false, isLegacy: false };
}

export default function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    let companiesMap = {};
    let adminsMap = {};
    const myUid = auth.currentUser?.uid;

    const merge = () => {
      const merged = Object.values(adminsMap)
        .filter(a => a.id !== myUid)
        .map(admin => {
          const co = companiesMap[admin.id] || {};
          return {
            id: admin.id,
            // Legacy admins can have companyId != uid; all their data is keyed by this
            companyId: admin.companyId || admin.id,
            companyName: co.companyName || '(No company name)',
            adminName: co.adminName || admin.name || '',
            adminEmail: co.adminEmail || admin.email || '',
            createdAt: co.createdAt || admin.createdAt || null,
            locked: co.locked || false,
            lockedAt: co.lockedAt || null,
            paid: co.paid || false,
            hasCompanyDoc: !!companiesMap[admin.id],
          };
        });
      merged.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setCompanies(merged);
      setLoading(false);
    };

    const unsubCompanies = onSnapshot(collection(db, 'companies'), snap => {
      companiesMap = {};
      snap.docs.forEach(d => { companiesMap[d.id] = { id: d.id, ...d.data() }; });
      merge();
    });

    const unsubUsers = onSnapshot(
      query(collection(db, 'users'), where('role', '==', 'admin')),
      snap => {
        adminsMap = {};
        snap.docs.forEach(d => { adminsMap[d.id] = { id: d.id, ...d.data() }; });
        merge();
      }
    );

    return () => { unsubCompanies(); unsubUsers(); };
  }, []);

  const handlePaid = async (company) => {
    if (!window.confirm(`Mark ${company.companyName} as paid? This removes the trial limit permanently.`)) return;
    try {
      await setDoc(doc(db, 'companies', company.id), { paid: true }, { merge: true });
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleRefund = async (company) => {
    if (!window.confirm(`Mark ${company.companyName} as refunded? This removes their paid status — if their free trial has already ended they'll lose access until they pay again.`)) return;
    try {
      await setDoc(doc(db, 'companies', company.id), { paid: false }, { merge: true });
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleLock = async (company) => {
    const willLock = !company.locked;
    const msg = willLock
      ? `Lock ${company.companyName}? They will immediately lose access.`
      : `Unlock ${company.companyName}? They will regain full access.`;
    if (!window.confirm(msg)) return;
    try {
      await setDoc(doc(db, 'companies', company.id), {
        locked: willLock,
        lockedAt: willLock ? new Date() : null,
      }, { merge: true });
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Delete every doc in `col` whose companyId == cid, in batches of 500
  // (Firestore's max per batched write).
  const deleteByCompany = async (col, cid) => {
    const snap = await getDocs(query(collection(db, col), where('companyId', '==', cid)));
    let batch = writeBatch(db);
    let n = 0;
    for (const d of snap.docs) {
      batch.delete(d.ref);
      if (++n === 500) { await batch.commit(); batch = writeBatch(db); n = 0; }
    }
    if (n > 0) await batch.commit();
  };

  const handleDelete = (company) => {
    setDeletePassword('');
    setDeleteError('');
    setDeleteTarget(company);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    const company = deleteTarget;
    if (!company) return;
    setDeleteError('');
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, deletePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
    } catch (err) {
      setDeleteError(
        err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
          ? 'Incorrect password.'
          : 'Error: ' + err.message
      );
      return;
    }
    setDeleteTarget(null);
    setDeletePassword('');
    setDeletingId(company.id);
    try {
      // The admin's own user doc shares the company's companyId, so clearing
      // the users collection removes the admin and all employees together.
      const cid = company.companyId;
      await deleteByCompany('users', cid);
      await deleteByCompany('timeEntries', cid);
      await deleteByCompany('shifts', cid);
      await deleteByCompany('timeOffRequests', cid);
      // Legacy accounts may have settings/company docs keyed by either uid or
      // companyId — delete both (deleting a nonexistent doc is a no-op).
      await deleteDoc(doc(db, 'settings', cid));
      await deleteDoc(doc(db, 'companies', company.id));
      if (cid !== company.id) {
        await deleteDoc(doc(db, 'settings', company.id));
        await deleteDoc(doc(db, 'companies', cid));
      }
    } catch (e) {
      alert('Error deleting: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = companies.filter(c => {
    const trial = getTrialStatus(c);
    if (filter === 'active' && (trial.ended || c.locked)) return false;
    if (filter === 'trial-ended' && (!trial.ended || c.locked)) return false;
    if (filter === 'locked' && !c.locked) return false;
    const q = search.toLowerCase();
    if (q && !c.companyName.toLowerCase().includes(q) && !c.adminEmail.toLowerCase().includes(q) && !c.adminName.toLowerCase().includes(q)) return false;
    return true;
  });

  const activeCount = companies.filter(c => !getTrialStatus(c).ended && !c.locked).length;
  const trialEndedCount = companies.filter(c => getTrialStatus(c).ended && !c.locked).length;
  const lockedCount = companies.filter(c => c.locked).length;

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.container}>
        <h1 style={s.title}>Companies</h1>

        <div style={s.statsRow}>
          <div style={s.statBox} onClick={() => setFilter('all')} className="stat-clickable">
            <div style={s.statNum}>{companies.length}</div>
            <div style={s.statLabel}>Total</div>
          </div>
          <div style={s.statBox} onClick={() => setFilter('active')}>
            <div style={{ ...s.statNum, color: '#4ade80' }}>{activeCount}</div>
            <div style={s.statLabel}>Active</div>
          </div>
          <div style={s.statBox} onClick={() => setFilter('trial-ended')}>
            <div style={{ ...s.statNum, color: '#fb923c' }}>{trialEndedCount}</div>
            <div style={s.statLabel}>Trial Ended</div>
          </div>
          <div style={s.statBox} onClick={() => setFilter('locked')}>
            <div style={{ ...s.statNum, color: '#f87171' }}>{lockedCount}</div>
            <div style={s.statLabel}>Locked</div>
          </div>
        </div>

        <div style={s.toolbar}>
          <input
            style={s.search}
            placeholder="Search company, admin name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={s.filterRow}>
            {['all', 'active', 'trial-ended', 'locked'].map(f => (
              <button
                key={f}
                style={{ ...s.filterBtn, ...(filter === f ? s.filterBtnActive : {}) }}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : f === 'trial-ended' ? 'Trial Ended' : 'Locked'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={s.empty}>No companies found.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {['Company', 'Admin', 'Joined', 'Trial Status', 'Lock Status', 'Action'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(company => {
                const trial = getTrialStatus(company);
                const borderColor = company.locked ? '#f87171' : trial.ended ? '#fb923c' : 'transparent';
                return (
                  <tr
                    key={company.id}
                    className="row-clickable"
                    style={{ ...s.tr, borderLeft: `3px solid ${borderColor}`, cursor: 'pointer' }}
                    onClick={() => navigate(`/company/${company.id}`)}
                    title="View employees"
                  >
                    <td style={s.td}>
                      <div style={s.companyName}>{company.companyName}</div>
                      <div style={s.viewHint}>View employees</div>
                    </td>
                    <td style={s.td}>
                      <div style={s.adminName}>{company.adminName}</div>
                      <div style={s.adminEmail}>{company.adminEmail}</div>
                    </td>
                    <td style={{ ...s.td, color: '#666', fontSize: 13 }}>
                      {company.createdAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                    </td>
                    <td style={s.td}>
                      {trial.isLegacy ? (
                        <span style={{ ...s.badge, backgroundColor: '#2a2a2a', color: '#666', border: '1px solid #333' }}>Legacy</span>
                      ) : (
                        <span style={{ ...s.badge, backgroundColor: trial.color + '22', color: trial.color, border: `1px solid ${trial.color}44` }}>
                          {trial.label}
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      {company.locked ? (
                        <span style={{ ...s.badge, backgroundColor: '#2d141422', color: '#f87171', border: '1px solid #f8717144' }}>Locked</span>
                      ) : (
                        <span style={{ ...s.badge, backgroundColor: '#14532d22', color: '#4ade80', border: '1px solid #4ade8044' }}>Active</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                        {company.paid ? (
                          <button style={s.refundBtn} onClick={() => handleRefund(company)}>
                            Refunded
                          </button>
                        ) : (
                          <button style={s.paidBtn} onClick={() => handlePaid(company)}>
                            Paid
                          </button>
                        )}
                        <button
                          style={company.locked ? s.unlockBtn : s.lockBtn}
                          onClick={() => handleLock(company)}
                        >
                          {company.locked ? 'Unlock' : 'Lock'}
                        </button>
                        <button
                          style={s.deleteBtn}
                          onClick={() => handleDelete(company)}
                          disabled={deletingId === company.id}
                        >
                          {deletingId === company.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <form style={s.modal} onClick={(e) => e.stopPropagation()} onSubmit={confirmDelete}>
            <h2 style={s.modalTitle}>Delete {deleteTarget.companyName}</h2>
            <p style={s.modalText}>
              This permanently deletes the admin ({deleteTarget.adminEmail || 'unknown email'}), every
              employee, all time entries, shifts, time-off requests and settings. This cannot be undone.
            </p>
            <p style={s.modalText}>Enter your superadmin password to confirm:</p>
            <input
              type="password"
              style={s.modalInput}
              placeholder="Password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              autoFocus
            />
            {deleteError && <div style={s.modalError}>{deleteError}</div>}
            <div style={s.modalActions}>
              <button type="button" style={s.cancelBtn} onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button type="submit" style={s.deleteBtn} disabled={!deletePassword}>
                Delete Permanently
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', backgroundColor: '#0f0f0f' },
  container: { padding: '32px 40px', maxWidth: 1200, margin: '0 auto' },
  title: { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 24 },
  statsRow: { display: 'flex', gap: 16, marginBottom: 32 },
  statBox: { flex: 1, backgroundColor: '#1e1e1e', borderRadius: 12, padding: 20, textAlign: 'center', cursor: 'pointer' },
  statNum: { fontSize: 32, fontWeight: 'bold', color: '#4f46e5', marginBottom: 4 },
  statLabel: { fontSize: 13, color: '#999' },
  toolbar: { display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' },
  search: { flex: 1, minWidth: 260, backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: 10, padding: '10px 16px', color: '#fff', fontSize: 14, outline: 'none' },
  filterRow: { display: 'flex', gap: 6 },
  filterBtn: { backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', color: '#999', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: '500' },
  filterBtnActive: { backgroundColor: '#4f46e5', color: '#fff', border: '1px solid #4f46e5' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#1e1e1e', borderRadius: 12, overflow: 'hidden' },
  th: { padding: '14px 20px', textAlign: 'left', color: '#999', fontSize: 12, fontWeight: '600', borderBottom: '1px solid #2a2a2a', textTransform: 'uppercase', letterSpacing: '0.5px' },
  tr: { borderBottom: '1px solid #2a2a2a' },
  td: { padding: '14px 20px', color: '#fff', fontSize: 14, verticalAlign: 'middle' },
  companyName: { fontWeight: 'bold', fontSize: 15 },
  viewHint: { color: '#4f46e5', fontSize: 11, marginTop: 2 },
  adminName: { color: '#ccc', fontSize: 14 },
  adminEmail: { color: '#666', fontSize: 12, marginTop: 2 },
  badge: { padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 'bold', whiteSpace: 'nowrap' },
  paidBtn: { backgroundColor: '#1e1b4b', color: '#818cf8', border: '1px solid #4f46e566', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  refundBtn: { backgroundColor: '#2a1e0f', color: '#fbbf24', border: '1px solid #fbbf2444', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  lockBtn: { backgroundColor: '#2d1414', color: '#f87171', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  unlockBtn: { backgroundColor: '#14532d', color: '#4ade80', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  deleteBtn: { backgroundColor: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  empty: { color: '#999', textAlign: 'center', padding: 60, fontSize: 15 },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16, zIndex: 200 },
  modal: { backgroundColor: '#161616', border: '1px solid #2a2a2a', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440 },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginBottom: 12 },
  modalText: { color: '#999', fontSize: 14, lineHeight: 1.5, marginBottom: 12 },
  modalInput: { width: '100%', backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: 10, padding: '10px 16px', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 12 },
  modalError: { color: '#f87171', fontSize: 13, marginBottom: 12 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  cancelBtn: { backgroundColor: 'transparent', color: '#999', border: '1px solid #333', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
};
